import { Action, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { addTime } from '../../../utils/dateTimeUtils';
import { State } from '../../../weborder/Store';
import {
  getCampaignProgress,
  getCollectionDetail,
  getCollectionLocationAreas,
  getCollectionSummaries,
  getDeliveryDetail,
  getDeliverySummaries,
} from '../../api/restaurant/restaurant.service';
import {
  Campaign,
  CollectionRestaurantDetail,
  CollectionRestaurantSummary,
  DeliveryRestaurantDetail,
  DeliveryRestaurantSummary,
  GetCampaignProgressQueryParams,
  GetCollectionLocationAreasQueryParams,
  GetCollectionSummariesQueryParams,
  GetDeliverySummariesQueryParams,
} from '../../api/restaurant/restaurant.types';
import { Async, AxiosError, Status } from '../../types/data.types';
import { OrderType } from '../../types/route.types';
import {
  SetRestaurantCollection,
  SetRestaurantDelivery,
} from './restaurant.types';
import {
  setCampaignProgress,
  setCollectionSummaries,
  setDeliverySummaries,
  setRestaurantCollection,
  setRestaurantCollectionLocationAreas,
  setRestaurantDelivery,
} from './restaurantActions';

export const loadCollectionSummaries =
  () =>
  (dispatch: Dispatch<Action>, getState: () => State): Promise<void> => {
    const { flipdish } = getState();

    const queryParams: GetCollectionSummariesQueryParams = {
      count: 1000,
      latitude: flipdish.Location.geo.Latitude,
      longitude: flipdish.Location.geo.Longitude,
      skip: 0,
    };

    const includePhysicalRestaurantIds =
      flipdish.Config.IncludePhysicalRestaurantIds;
    if (includePhysicalRestaurantIds) {
      queryParams.includePhysicalRestaurantIds = includePhysicalRestaurantIds;
    }

    return getCollectionSummaries(queryParams)
      .then((data: CollectionRestaurantSummary[]) => {
        dispatch(
          setCollectionSummaries({
            data,
            status: 'success',
          }),
        );
      })
      .catch((_error: AxiosError) => {
        dispatch(
          setCollectionSummaries({
            status: 'none',
          }),
        );
      });
  };

export const loadDeliverySummaries =
  () =>
  (dispatch: Dispatch<Action>, getState: () => State): Promise<void> => {
    const { flipdish } = getState();

    const queryParams: GetDeliverySummariesQueryParams = {
      count: 1000,
      deviceId: flipdish.Config.DeviceId,
      latitude: flipdish.Location.geo.Latitude,
      longitude: flipdish.Location.geo.Longitude,
      skip: 0,
    };

    return getDeliverySummaries(queryParams)
      .then((data: DeliveryRestaurantSummary[]) => {
        dispatch(
          setDeliverySummaries({
            data,
            status: 'success',
          }),
        );
      })
      .catch((_error: AxiosError) => {
        dispatch(
          setDeliverySummaries({
            status: 'none',
          }),
        );
      });
  };

export const loadRestaurantDetail =
  (
    vrId: number,
    orderType: OrderType.COLLECTION | OrderType.DELIVERY,
    id: number,
  ) =>
  (dispatchThunk: ThunkDispatch<State, {}, Action>): void => {
    if (orderType === OrderType.COLLECTION) {
      dispatchThunk(loadCollectionRestaurantDetail(id));
    } else {
      dispatchThunk(loadDeliveryRestaurantDetail(vrId, id));
    }
  };

export const loadCollectionRestaurantDetail =
  (prId: number) =>
  (dispatch: Dispatch<Action>): Promise<void> => {
    const dispatchSetRestaurantCollection = (
      collectionRestaurant: Async<CollectionRestaurantDetail>,
    ): SetRestaurantCollection =>
      dispatch(setRestaurantCollection(prId, collectionRestaurant));

    dispatchSetRestaurantCollection({ status: Status.LOADING });

    return getCollectionDetail(prId)
      .then((data: CollectionRestaurantDetail) => {
        const closingTimeStamp = data?.SecondsUntilRestaurantCloses
          ? addTime(new Date(), 'seconds', data.SecondsUntilRestaurantCloses)
          : undefined;

        const customData = {
          ...data,
          ClosingTimeStamp: closingTimeStamp,
        };

        dispatchSetRestaurantCollection({
          status: Status.SUCCESS,
          data: customData,
        });
      })
      .catch((error: AxiosError) => {
        dispatchSetRestaurantCollection({
          status: Status.ERROR,
          error,
        });
      });
  };

export const loadDeliveryRestaurantDetail =
  (vrId: number, deliveryLocationId: number) =>
  (dispatch: Dispatch<Action>, getState: () => State): Promise<void> => {
    const { flipdish } = getState();

    const dispatchSetRestaurantDelivery = (
      deliveryRestaurant: Async<DeliveryRestaurantDetail>,
    ): SetRestaurantDelivery =>
      dispatch(
        setRestaurantDelivery(deliveryLocationId, deliveryRestaurant, vrId),
      );

    dispatchSetRestaurantDelivery({ status: Status.LOADING });

    const queryParams = {
      deliveryLocationId,
      deviceId: flipdish.Config.DeviceId,
    };

    return getDeliveryDetail(vrId, queryParams)
      .then((data: DeliveryRestaurantDetail) => {
        const closingTimeStamp = data?.SecondsUntilRestaurantCloses
          ? addTime(new Date(), 'seconds', data.SecondsUntilRestaurantCloses)
          : undefined;

        const customData = {
          ...data,
          ClosingTimeStamp: closingTimeStamp,
        };

        dispatchSetRestaurantDelivery({
          status: Status.SUCCESS,
          data: customData,
        });
      })
      .catch((error: AxiosError) => {
        dispatchSetRestaurantDelivery({
          status: Status.ERROR,
          error,
        });
      });
  };

export const loadCampaignProgress =
  (queryParams: GetCampaignProgressQueryParams) =>
  (dispatch: Dispatch<Action>): Promise<void> =>
    getCampaignProgress(queryParams)
      .then((data: Campaign[]) => {
        dispatch(
          setCampaignProgress({
            data,
            status: 'success',
            prId: queryParams.storeIDs,
          }),
        );
      })
      .catch((_error: AxiosError) => {
        setCampaignProgress({
          status: 'none',
          prId: queryParams.storeIDs,
        });
      });

export const loadCollectionLocationAreas =
  (queryParams: GetCollectionLocationAreasQueryParams) =>
  (dispatch: Dispatch<Action>): Promise<void> =>
    getCollectionLocationAreas(queryParams).then((data) => {
      dispatch(setRestaurantCollectionLocationAreas(data, queryParams.storeId));
    });
